/**
 * useOperatorPrefs — calm, lightweight preference memory for
 * operational coordination surfaces (Deal Rooms, Sales Progression…).
 *
 * Doctrine:
 *   - Persist ONLY operator view choices (sort mode, grouping,
 *     active operational filter, board/list view).
 *   - NEVER persist search queries, modal state, or transient
 *     overlays.
 *   - localStorage scoped by `surface + orgId + userId` so a user
 *     switching workspaces sees their own memory, not a leaked
 *     preference from the previous tenant.
 *   - Read once on mount, write on every change. No subscriptions,
 *     no broadcast — invisible and calm.
 *   - Graceful when localStorage is unavailable (private mode,
 *     SSR, quota errors).
 *
 * The hook surface is deliberately small: one initial snapshot
 * (`initial`) for state-initialiser use, one writer (`persist`),
 * and a `restoredKeys` Set so the caller can decide whether to
 * surface a calm "Restored: …" caption.
 */
import { useCallback, useMemo, useRef } from "react";

const STORAGE_NS = "chainflow:opPrefs";

export function operatorPrefsKey(surface, orgId, userId) {
  // `_` is the anonymous fallback so the key is always parseable
  // even before the auth user has hydrated.
  return `${STORAGE_NS}:${surface}:${orgId || "_"}:${userId || "_"}`;
}

export function readOperatorPrefs(surface, orgId, userId) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(operatorPrefsKey(surface, orgId, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeOperatorPrefs(surface, orgId, userId, prefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      operatorPrefsKey(surface, orgId, userId),
      JSON.stringify(prefs || {}),
    );
  } catch {
    /* private mode / quota — operator stays calm */
  }
}

/**
 * useOperatorPrefs(surface, orgId, userId)
 *
 * Returns:
 *   initial:      the snapshot read on first mount (or {} if none).
 *                 Safe to use directly inside `useState` initialisers.
 *   restoredKeys: Set<string> of keys that were present in storage on
 *                 this mount. Empty for first-time operators.
 *   persist:      (patch) => void — merges `patch` into stored prefs
 *                 and flushes to localStorage. Patches are merged so
 *                 callers can persist one slice at a time without
 *                 reading the whole blob.
 */
export function useOperatorPrefs(surface, orgId, userId) {
  // Read storage exactly once per mount. The ref keeps the snapshot
  // stable across renders even if orgId/userId hydrate after mount
  // (auth bootstrap), so the restore caption never flickers.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    initialRef.current = readOperatorPrefs(surface, orgId, userId) || {};
  }
  const restoredKeys = useMemo(
    () => new Set(Object.keys(initialRef.current)),
    [],
  );

  // Local mirror so `persist` patches compose without re-reading
  // localStorage on every call.
  const mirrorRef = useRef({ ...initialRef.current });

  const persist = useCallback(
    (patch) => {
      if (!patch || typeof patch !== "object") return;
      mirrorRef.current = { ...mirrorRef.current, ...patch };
      writeOperatorPrefs(surface, orgId, userId, mirrorRef.current);
    },
    [surface, orgId, userId],
  );

  return {
    initial: initialRef.current,
    restoredKeys,
    persist,
  };
}
